import React, { useContext, useState } from "react";
import { MediaQueriesContext, AuthContext } from "@/App.jsx";
import { useHistory, useLocation } from "react-router-dom";

import _ from "lodash";
import Button from "Components/button/Button";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";

import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";

import TranslatedString from "Components/TranslatedString";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import { Icon, plus, globe, addDomain, emailDelivery } from "Icons/index";
import DomainDeliveryRow from "Components/domainRow/DomainDeliveryRow";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import s from "../delivery.scss";
import { t } from "i18next";
import useQueryParams from "Utils/useQueryParams";
import { alertError, alertSuccess } from "Utils/alerts";
import { useApiMutation, useApiQuery } from "../../../hooks";

const filters = ["all", "enabled", "disabled"];

const DeliveryDomainsListScreen = function () {
  const history = useHistory();
  const media = useContext(MediaQueriesContext);
  const auth = useContext(AuthContext);
  const location = useLocation();
  const loggedUser = auth.getLoggedUser();
  const searchParams = useQueryParams();
  const [domainToDelete, setDomainToDelete] = useState(null);
  const [isForbiddenModalOpen, setIsForbiddenModalOpen] = useState(false);

  const {
    data: deliveryDomains,
    isLoading: isDeliveryDomainsLoading,
    refreshData: refreshDeliveryDomains,
    isRefreshing: isDeliveryDomainsRefreshing,
    getData: getDeliveryDomains,
    pagination,
  } = useApiQuery("getDeliveryDomains", {
    enablePagination: true,
  });

  const { mutate: deleteDeliveryDomain, isLoading: isDeletingDeliveryDomain } = useApiMutation("deleteDeliveryDomain");
  const addNew = () => {
    history.push("/delivery/domains/new");
  };

  const deleteDomain = async () => {
    if (!canDeleteDomain) {
      setIsForbiddenModalOpen(true);
      return;
    }
    try {
      await deleteDeliveryDomain(domainToDelete.code);
      await refreshDeliveryDomains();
      alertSuccess(t("alert.operationSuccess"));
    } catch (error) {
      console.error(error);
      alertError(t("alert.operationError"));
    } finally {
      setDomainToDelete(null);
    }
  };

  const canCreateDomain = !(
    loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("create_delivery_domains")
  );

  const canDeleteDomain = !(
    loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("delete_delivery_domains")
  );

  return (
    <div className="screenContainer">
      <div className={s.listHeader}>
        <div>
          <div className="sectionTitle">{t("delivery.domains.list.title")}</div>

          {!media.isMediaMobile && <div className="sectionDesc marginRight80">{t("delivery.domains.list.desc")}</div>}
        </div>

        <div className={s.actionsList}>
          <Button docsKey="deliveryDomains" color="docs" className={s.btnList} onlyIcon={media.isMediaMobile}>
            button.docs
          </Button>

          {canCreateDomain && (
            <Button icon={plus} onClick={addNew} onlyIcon={media.isMediaMobile}>
              button.addDomain
            </Button>
          )}
        </div>
      </div>

      <FullPaginationControls
        className={s.filters}
        paginationInfo={pagination || {}}
        location={location}
        history={history}
        refreshList={() => getDeliveryDomains()}
        listFilters={filters}
        listFilterLegends={{
          all: <Icon svg={globe} width="0.75rem" height="0.75rem" />,
          enabled: <DomainStatusBadge status="enabled" isHealthy showAsPreview />,
          disabled: <DomainStatusBadge status="disabled" isHealthy showAsPreview />,
        }}
        showCustomerSelection={true}
        isLoading={!isDeliveryDomainsRefreshing && isDeliveryDomainsLoading}
        totals={pagination && pagination.totals}
      />
      <div className={s.contentList}>
        <div className={s.listContainer}>
          <ResponsiveTable
            history={history}
            areRowsLoading={isDeliveryDomainsLoading}
            location={location}
            addIcon={addDomain}
            addMsg={t("table.row.add.msg.domain")}
            addDesc={t("table.row.add.desc.domain")}
            headers={_.compact([
              {
                name: t("table.header.status"),
                size: media.isMobileDevice ? "2rem" : "4rem",
                center: true,
              },
              {
                name: t("table.header.domain"),
                key: "name",
                sortable: true,
                activeSorting: searchParams.get("sort_field") === "name" && searchParams.get("sort"),
              },

              loggedUser.type === "Admin"
                ? {
                    name: t("table.header.customer"),
                    key: "customer_name",
                    hideOnMobile: true,
                  }
                : null,

              {
                name: t("table.header.verification"),
                hideOnMobile: true,
              },

              {
                name: t("table.header.deliveryDomain.accounts"),
                hideOnMobile: true,
                sortable: true,
                key: "accounts_count",
                activeSorting: searchParams.get("sort_field") === "accounts_count" && searchParams.get("sort"),
              },

              { size: "3rem", center: true, hideOnMobile: true },
              { size: "3rem", center: true },
            ])}
            rows={_.map(deliveryDomains, (dom) => {
              return (
                <DomainDeliveryRow
                  key={dom.code}
                  location={location}
                  domain={dom}
                  isLoggedUserAdmin={loggedUser.type === "Admin"}
                  history={history}
                  disableDomain={() => {}}
                  deleteDomain={(dom) => {
                    if (!canDeleteDomain) {
                      setIsForbiddenModalOpen(true);
                      return;
                    }

                    setDomainToDelete(dom);
                  }}
                  changeDomainStatus={() => {}}
                />
              );
            })}
          />
        </div>
      </div>
      <ConfirmModal
        title={`${t("modal.delete.deliveryDomain.title")}`}
        text={
          <div style={{ display: "flex" }}>
            <div className={s.modalIcon}>
              <Icon svg={emailDelivery} width="1rem" />
            </div>
            <TranslatedString subs={{ domainName: domainToDelete?.name }}>
              modal.delete.deliveryDomain.text
            </TranslatedString>
          </div>
        }
        textButton={`button.delete`}
        opened={Boolean(domainToDelete)}
        action={deleteDomain}
        handleClose={() => setDomainToDelete(null)}
        isLoading={isDeletingDeliveryDomain}
      />

      <ConfirmModal
        title={t("teamMembers.forbiddenAction")}
        text={t("teamMembers.forbiddenActionSubtitle")}
        textButton="button.ok"
        hideCancelButton={true}
        opened={isForbiddenModalOpen}
        action={() => setIsForbiddenModalOpen(false)}
        handleClose={() => setIsForbiddenModalOpen(false)}
        buttonColor="accent"
      />
    </div>
  );
};

export default DeliveryDomainsListScreen;
